import React, { useCallback, useEffect, useState } from 'react';
import {
  UserIcon,
  DocumentTextIcon,
  CogIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  PencilIcon,
  TrashIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';
import { DocumentVersion, FilterStatus } from '../types/documentVersion.types';
import apiClient from '@/api/axios';
import { 
  formatVersionLabel,
  getVersionDisplayName, 
  getUserDisplayName, 
  getVersionStats, 
  getVersionStatusInfo, 
  getRelativeTime, 
  groupVersionsByDate 
} from '../utils/versionUtils';
import CollaborationManager from '../../CollaborationManager';

// Removed CheckoutStatus interface - each CollaborationManager handles its own checkout state

interface VersionsListProps {
  versions: DocumentVersion[];
  currentVersionId?: string;
  currentDocumentStats?: {
    wordCount: number;
    characterCount: number;
    pageCount: number;
  };
  contacts: any[];
  currentUserInfo: any;
  keycloakTokenParsed: any;
  documentId: string;
  documentTitle?: string;
  currentVersion?: string;
  
  // Search and filter
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterStatus: FilterStatus;
  setFilterStatus: (status: FilterStatus) => void;
  
  // Selection
  selectedVersions: string[];
  onToggleVersionSelection: (versionId: string) => void;
  onClearSelection: () => void;
  
  // Actions
  onVersionSelect: (version: DocumentVersion) => void;
  onDownloadVersion: (version: DocumentVersion) => void;
  onPromoteVersion: (version: DocumentVersion) => void;
  onDeleteVersion: (version: DocumentVersion) => void;
  onBulkDownload: () => void;
  onVersionComparison: () => void;
  onCompareWithActive: (versionId: string) => void;
  onEditVersion: (versionId?: string) => void; // 🎯 NEW: Edit version function
  
  // 🎯 Checkout functionality - removed shared status, each CollaborationManager handles its own
  onCheckoutDocument?: () => void;
  onCheckinDocument?: () => void;
  
  // 🎯 Refresh callbacks for after checkin operations
  onDocumentUpdate?: () => void;
  onVersionsRefresh?: () => void;
  
  // Removed sharedCollaborators - each CollaborationManager loads its own version-specific data
}

type SignatureAuditCardMetadata = {
  sessionId: string | null;
  committedAt: string | null;
  policyLabel: string;
  evidenceHash: string | null;
  eventChainValid: boolean | null;
  exportEndpoint: string | null;
  generatedAt: string | null;
  signerNames: string[];
};

type VersionEvidencePreviewState = {
  loading: boolean;
  loaded: boolean;
  canDownload: boolean;
  accessDenied: boolean;
  signerNames: string[];
  latestSignedAt: string | null;
  error: string | null;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function parseSignatureAuditCardMetadata(version: DocumentVersion): SignatureAuditCardMetadata | null {
  const checkinMetadata = asRecord(version.checkin_metadata);
  const signatureAudit = asRecord(checkinMetadata.signature_audit);
  if (Object.keys(signatureAudit).length === 0) {
    return null;
  }

  const evidence = asRecord(signatureAudit.evidence);
  const signerSummary = asRecord(signatureAudit.signer_summary);
  const signedUsers = Array.isArray(signerSummary.signed_users) ? signerSummary.signed_users : [];
  const signerNames = signedUsers
    .map((entry) => normalizeString(asRecord(entry).display_name))
    .filter((entry): entry is string => Boolean(entry));

  const policyProfileKey = normalizeString(signatureAudit.policy_profile_key);
  const policyProfileId = normalizeString(signatureAudit.policy_profile_id);

  return {
    sessionId: normalizeString(signatureAudit.session_id),
    committedAt: normalizeString(signatureAudit.committed_at),
    policyLabel: policyProfileKey || policyProfileId || 'Not specified',
    evidenceHash: normalizeString(evidence.evidence_hash),
    eventChainValid: normalizeBoolean(evidence.event_chain_valid),
    exportEndpoint: normalizeString(evidence.export_endpoint),
    generatedAt: normalizeString(evidence.generated_at),
    signerNames,
  };
}

function parseEvidenceSigners(evidence: unknown): string[] {
  const evidenceRecord = asRecord(evidence);
  const fields = Array.isArray(evidenceRecord.fields) ? evidenceRecord.fields : [];
  const names = new Set<string>();

  for (const rawField of fields) {
    const field = asRecord(rawField);
    const signedByDisplay = normalizeString(field.signed_by_display);
    const signerDisplay = normalizeString(field.signer_display);
    if (signedByDisplay) {
      names.add(signedByDisplay);
      continue;
    }
    if (signerDisplay) {
      names.add(signerDisplay);
    }
  }

  return Array.from(names);
}

function parseLatestSignedAt(evidence: unknown): string | null {
  const evidenceRecord = asRecord(evidence);
  const fields = Array.isArray(evidenceRecord.fields) ? evidenceRecord.fields : [];
  let latest: string | null = null;

  for (const rawField of fields) {
    const field = asRecord(rawField);
    const signedAt = normalizeString(field.signed_at);
    if (!signedAt) continue;
    if (!latest || signedAt > latest) {
      latest = signedAt;
    }
  }

  return latest;
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatEvidenceHash(value: string | null): string {
  if (!value) return 'Not available';
  if (value.length <= 20) return value;
  return `${value.slice(0, 12)}...${value.slice(-8)}`;
}

function extractDownloadFileName(contentDisposition: unknown, fallback: string): string {
  if (typeof contentDisposition !== 'string') {
    return fallback;
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }
  return fallback;
}

export const VersionsList: React.FC<VersionsListProps> = ({
  versions,
  currentVersionId,
  currentDocumentStats,
  contacts,
  currentUserInfo,
  keycloakTokenParsed,
  documentId,
  documentTitle,
  currentVersion,
  searchTerm,
  setSearchTerm,
  filterStatus,
  setFilterStatus,
  selectedVersions,
  onToggleVersionSelection,
  onClearSelection,
  // onVersionSelect, // Removed: now using direct editor navigation
  onDownloadVersion,
  onPromoteVersion,
  onDeleteVersion,
  onBulkDownload,
  onVersionComparison,
  onCompareWithActive,
  onEditVersion, // 🎯 NEW: Edit version function
  
  // 🎯 Checkout functionality - removed shared status
  onCheckoutDocument,
  onCheckinDocument,
  
  // 🎯 Refresh callbacks
  onDocumentUpdate,
  onVersionsRefresh,
}) => {
  const [groupByDate, setGroupByDate] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [showActionMenu, setShowActionMenu] = useState<Record<string, boolean>>({});
  const [evidencePreviewByVersion, setEvidencePreviewByVersion] = useState<Record<string, VersionEvidencePreviewState>>({});
  const [evidenceDownloadByVersion, setEvidenceDownloadByVersion] = useState<Record<string, boolean>>({});

  const isWordVersion = (version: DocumentVersion): boolean => {
    const mimeType = (version.mime_type || '').toLowerCase();
    const originalName = (version.original_name || documentTitle || '').toLowerCase();
    return (
      mimeType.includes('word') ||
      mimeType.includes('msword') ||
      mimeType.includes('wordprocessingml') ||
      originalName.endsWith('.docx') ||
      originalName.endsWith('.doc')
    );
  };

  // Filter versions for search and status
  const filteredVersions = versions.filter(version => {
    const matchesSearch = version.changes_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         version.original_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'major' && version.changes_description?.includes('major')) ||
                         (filterStatus === 'minor' && !version.changes_description?.includes('major'));
    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    filteredVersions.forEach((version) => {
      const signatureAudit = parseSignatureAuditCardMetadata(version);
      if (!signatureAudit?.exportEndpoint) {
        return;
      }

      const versionState = evidencePreviewByVersion[version.version_id];
      if (versionState?.loading || versionState?.loaded) {
        return;
      }

      setEvidencePreviewByVersion((previous) => {
        const existing = previous[version.version_id];
        if (existing?.loading || existing?.loaded) {
          return previous;
        }
        return {
          ...previous,
          [version.version_id]: {
            loading: true,
            loaded: false,
            canDownload: false,
            accessDenied: false,
            signerNames: signatureAudit.signerNames,
            latestSignedAt: null,
            error: null,
          },
        };
      });

      void (async () => {
        try {
          const response = await apiClient.get(signatureAudit.exportEndpoint!, {
            params: {
              download: 'false',
              includeEventData: 'false',
            },
          });
          const evidence = response.data?.evidence;
          const signerNames = parseEvidenceSigners(evidence);
          const latestSignedAt = parseLatestSignedAt(evidence);

          setEvidencePreviewByVersion((previous) => ({
            ...previous,
            [version.version_id]: {
              loading: false,
              loaded: true,
              canDownload: true,
              accessDenied: false,
              signerNames: signerNames.length > 0 ? signerNames : signatureAudit.signerNames,
              latestSignedAt,
              error: null,
            },
          }));
        } catch (error) {
          const status = (error as any)?.response?.status;
          const denied = status === 403;
          const message = denied
            ? 'You do not have permission to download this evidence package.'
            : ((error as any)?.response?.data?.message || (error as Error)?.message || 'Failed to load evidence metadata.');

          setEvidencePreviewByVersion((previous) => ({
            ...previous,
            [version.version_id]: {
              loading: false,
              loaded: true,
              canDownload: !denied,
              accessDenied: denied,
              signerNames: signatureAudit.signerNames,
              latestSignedAt: null,
              error: denied ? null : message,
            },
          }));
        }
      })();
    });
  }, [evidencePreviewByVersion, filteredVersions]);

  const downloadEvidencePackage = useCallback(async (version: DocumentVersion, exportEndpoint: string) => {
    setEvidenceDownloadByVersion((previous) => ({ ...previous, [version.version_id]: true }));
    setEvidencePreviewByVersion((previous) => ({
      ...previous,
      [version.version_id]: {
        ...(previous[version.version_id] || {
          loading: false,
          loaded: true,
          canDownload: true,
          accessDenied: false,
          signerNames: [],
          latestSignedAt: null,
          error: null,
        }),
        error: null,
      },
    }));

    try {
      const response = await apiClient.get(exportEndpoint, { responseType: 'arraybuffer' });
      const contentDisposition = response.headers?.['content-disposition'];
      const fallbackName = `signature-evidence-${version.document_id}-${version.version_id}.json`;
      const fileName = extractDownloadFileName(contentDisposition, fallbackName);
      const blob = new Blob([response.data], { type: 'application/json;charset=utf-8' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const status = (error as any)?.response?.status;
      const denied = status === 403;
      const message = denied
        ? 'You do not have permission to download this evidence package.'
        : ((error as any)?.response?.data?.message || (error as Error)?.message || 'Failed to download evidence package.');

      setEvidencePreviewByVersion((previous) => ({
        ...previous,
        [version.version_id]: {
          ...(previous[version.version_id] || {
            loading: false,
            loaded: true,
            canDownload: false,
            accessDenied: denied,
            signerNames: [],
            latestSignedAt: null,
            error: null,
          }),
          canDownload: !denied,
          accessDenied: denied,
          error: denied ? null : message,
        },
      }));
    } finally {
      setEvidenceDownloadByVersion((previous) => ({ ...previous, [version.version_id]: false }));
    }
  }, []);

  const renderVersionCard = (version: DocumentVersion, index: number) => {
    const isCurrentVersion = version.version_id === currentVersionId;
    const isLatestVersion = index === 0;
    const canPromote = !isCurrentVersion && versions.length > 1;
    const statusInfo = getVersionStatusInfo(version, isCurrentVersion, isLatestVersion, currentVersionId);
    const stats = getVersionStats(version, isCurrentVersion, currentDocumentStats);
    const userDisplayName = getUserDisplayName(version.uploaded_by_user_id, contacts, currentUserInfo, keycloakTokenParsed);
    const displayName = getVersionDisplayName(version);
    const versionLabel = formatVersionLabel(version.version_number);
    const relativeTime = getRelativeTime(version.created_at);
    const signatureAudit = parseSignatureAuditCardMetadata(version);
    const evidencePreview = evidencePreviewByVersion[version.version_id];
    const isDownloadingEvidence = Boolean(evidenceDownloadByVersion[version.version_id]);
    const signerNames = (evidencePreview?.signerNames && evidencePreview.signerNames.length > 0)
      ? evidencePreview.signerNames
      : signatureAudit?.signerNames || [];
    const signedAt = evidencePreview?.latestSignedAt || signatureAudit?.committedAt || null;
    const opensInWord = isWordVersion(version);

    return (
      <div
        key={version.version_id}
        className={`group p-4 border rounded-lg transition-all duration-200 ${
          isCurrentVersion
            ? 'border-lex-accent bg-lex-accent-subtle shadow-md'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Version Header */}
            <div className="flex items-center space-x-3 mb-2">
              <input
                type="checkbox"
                checked={selectedVersions.includes(version.version_id)}
                onChange={() => onToggleVersionSelection(version.version_id)}
                className="h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
              />
              
              {/* Visual Status Indicators */}
              <div className="flex items-center space-x-2">
                <span className="text-lg">{statusInfo.icon}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {versionLabel}
                </span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  statusInfo.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  statusInfo.color === 'blue' ? 'bg-lex-accent-soft text-lex-accent' :
                  statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                }`}>
                  {statusInfo.badge}
                </span>
              </div>
              
              {/* Relative timestamps */}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ⏰ {relativeTime}
              </span>
            </div>

            {/* Clean display name */}
            <div className="mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {displayName}
              </h4>
              {version.changes_description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {version.changes_description}
                </p>
              )}
            </div>

            {/* Enhanced metadata */}
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center space-x-1">
                <UserIcon className="h-3 w-3" />
                <span>{userDisplayName}</span>
              </span>
              <span className="flex items-center space-x-1">
                <DocumentTextIcon className="h-3 w-3" />
                <span>📝 {stats.words.toLocaleString()} words • {stats.pages} page{stats.pages !== 1 ? 's' : ''}</span>
              </span>
              <span className="flex items-center space-x-1">
                <span>📁 {stats.sizeDisplay}</span>
              </span>
            </div>

            {signatureAudit && (
              <div className="mt-3 rounded border border-emerald-200 bg-emerald-50/50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Signature Evidence</h5>
                    <p className="mt-0.5 text-[11px] text-emerald-700">
                      Policy: <span className="font-medium">{signatureAudit.policyLabel}</span>
                    </p>
                  </div>
                  {signatureAudit.exportEndpoint && (
                    <button
                      type="button"
                      onClick={() => void downloadEvidencePackage(version, signatureAudit.exportEndpoint!)}
                      disabled={Boolean(evidencePreview?.loading) || !evidencePreview?.canDownload || isDownloadingEvidence}
                      className="inline-flex items-center rounded border border-emerald-300 bg-white px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                      title={evidencePreview?.accessDenied ? 'Evidence access is restricted to session owner/admin.' : 'Download evidence package'}
                    >
                      <ArrowDownTrayIcon className="mr-1 h-3.5 w-3.5" />
                      {isDownloadingEvidence ? 'Downloading...' : 'Download Evidence'}
                    </button>
                  )}
                </div>

                <div className="mt-2 space-y-1 text-[11px] text-emerald-800">
                  <p>
                    <span className="font-medium">Signer(s):</span>{' '}
                    {signerNames.length > 0 ? signerNames.join(', ') : 'Signer details are in the evidence package.'}
                  </p>
                  <p>
                    <span className="font-medium">Signed/Committed:</span> {formatDateTime(signedAt)}
                  </p>
                  <p>
                    <span className="font-medium">Evidence Hash:</span> {formatEvidenceHash(signatureAudit.evidenceHash)}
                  </p>
                  <p>
                    <span className="font-medium">Chain Status:</span>{' '}
                    {signatureAudit.eventChainValid === true ? 'Valid' : signatureAudit.eventChainValid === false ? 'Invalid' : 'Not available'}
                  </p>
                </div>

                {evidencePreview?.loading && (
                  <p className="mt-2 text-[11px] text-emerald-700">Checking evidence access permissions...</p>
                )}
                {evidencePreview?.accessDenied && (
                  <p className="mt-2 text-[11px] text-amber-700">
                    Evidence download is restricted to the session owner or a firm admin.
                  </p>
                )}
                {evidencePreview?.error && (
                  <p className="mt-2 text-[11px] text-red-700">{evidencePreview.error}</p>
                )}
              </div>
            )}

            {/* Collaboration Panel */}
            <CollaborationManager
              documentId={documentId}
              versionId={version.version_id}
              isCurrentVersion={isCurrentVersion}
              ownerPermissions={false} // TODO: Implement proper owner check based on document collaboration role
              className="mt-3"
              documentTitle={documentTitle}
              currentVersion={currentVersion || version.version_number}
              onManagePermissions={(userId) => {
                console.log('Manage permissions for user:', userId);
              }}
              onDocumentUpdate={onDocumentUpdate}
              onVersionsRefresh={onVersionsRefresh}
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-end space-y-2 ml-4">
            {/* 🎯 CONSOLIDATED: Single Open button for both current and historical versions */}
            <button
              onClick={() => onEditVersion(version.version_id)}
              className="px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover"
              title={opensInWord ? `Open Version ${version.version_number} in Word with EdgeLex tracking` : `Open Version ${version.version_number} in Editor`}
            >
              <PencilIcon className="h-4 w-4" />
              {opensInWord ? 'Open in Word' : 'Open'}
            </button>

            
            {/* Enhanced Actions Dropdown */}
            <div className="relative action-menu-container">
              <button
                onClick={() => setShowActionMenu(prev => ({ 
                  ...prev, 
                  [version.version_id]: !prev[version.version_id] 
                }))}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="More actions"
              >
                <CogIcon className="h-4 w-4" />
              </button>
              
              {showActionMenu[version.version_id] && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => onDownloadVersion(version)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Download
                    </button>
                    <button
                      onClick={canPromote ? () => onPromoteVersion(version) : undefined}
                      disabled={!canPromote}
                      className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                        canPromote 
                          ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                          : 'text-gray-400 dark:text-gray-500 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
                      }`}
                      title={
                        isCurrentVersion 
                          ? "This version is already active"
                          : "Restore this version as the active document"
                      }
                    >
                      <ArrowUpIcon className="h-4 w-4 mr-2" />
                      Restore as Active
                    </button>
                    <button
                      onClick={() => onCompareWithActive(version.version_id)}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <ArrowsUpDownIcon className="h-4 w-4 mr-2" />
                      Compare with Active
                    </button>
                    <button
                      onClick={() => {/* TODO: Add version notes */}}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Add Notes
                    </button>
                    {versions.length > 1 && (
                      <>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <button
                          onClick={() => onDeleteVersion(version)}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          Delete Version
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-6">
      {/* 🎯 Removed shared checkout status notification - each CollaborationManager handles its own */}

      {/* Search, Filter and Bulk Actions */}
      <div className="space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search versions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Versions</option>
            <option value="major">Major Changes</option>
            <option value="minor">Minor Changes</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedVersions.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-lex-accent-soft rounded-lg">
            <span className="text-sm text-lex-accent">
              {selectedVersions.length} version(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={onBulkDownload}
                className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
              >
                Download All
              </button>
              {selectedVersions.length === 2 && (
                <button
                  onClick={onVersionComparison}
                  className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
                >
                  Compare
                </button>
              )}
              <button
                onClick={onClearSelection}
                className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Version List */}
      <div className="space-y-1">
        {/* Grouping Toggle */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setGroupByDate(!groupByDate)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                groupByDate 
                  ? 'bg-lex-accent-soft text-lex-accent'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              📅 Group by Date
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredVersions.length} version{filteredVersions.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {groupByDate ? (
            // Smart Date Grouping
            Object.entries(groupVersionsByDate(filteredVersions)).map(([groupName, groupVersions]) => (
              <div key={groupName} className="space-y-2">
                {/* Group Header */}
                <button
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }))}
                  className="flex items-center justify-between w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {groupName} ({groupVersions.length} version{groupVersions.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <span className="text-gray-400 dark:text-gray-500">
                    {collapsedGroups[groupName] ? '▶' : '▼'}
                  </span>
                </button>

                {/* Group Content */}
                {!collapsedGroups[groupName] && (
                  <div className="space-y-2 ml-4">
                    {groupVersions.map((version, index) => renderVersionCard(version, index))}
                  </div>
                )}
              </div>
            ))
          ) : (
            // Simple list without grouping
            filteredVersions.map((version, index) => renderVersionCard(version, index))
          )}
        </div>
      </div>
    </div>
  );
};
